import { IconButton, MenuItem, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Modal from '@mui/material/Modal';
import Select from '@mui/material/Select';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux-latest';
import { useNavigate } from 'react-router-dom';
import { InfoIcon } from '../../components/icons/info';
import { handleMoveProspects } from '../../redux/actions';
import { purchaseProspects } from '../../redux/ducks/prospect-search-result/operations';
import { AppDispatch, RootState } from '../../redux/store/configureStore';
import { paths } from '../../routes/paths';
import CloseIcon from '../icons/close';
import { MoveProspectsToCampaignContainer } from './moveProspectsToCampaign.styled';
import { useProspectCollections } from '../../hooks/use-prospect-collections';

type Props = {
    onModalClose: () => void;
    onMoveProspects: () => void;
    campaignId?: string;
    selectedProspects: readonly string[];
    moveAndPurchase?: boolean;
    shouldShowNumbers?: boolean;
    moveSelectedProspects?: boolean;
    moveFromProspectRow?: boolean;
    prospect?: object;
    text: string;
    prospectDrawer?: boolean;
};

type CanMoveProps = {
    allowedToMove: string[];
    notAllowedToMove: {
        id: string;
        reason: string;
    }[];
};

export function MoveProspectsToCampaign({
    onModalClose,
    onMoveProspects,
    campaignId,
    selectedProspects,
    moveAndPurchase,
    shouldShowNumbers,
    moveSelectedProspects,
    moveFromProspectRow,
    prospect,
    text,
    prospectDrawer,
}: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const movingProspects = useSelector((state: RootState) => state.ui.movingProspects);
    const { prospectCollections } = useProspectCollections();
    // const pagesCount = useAppSelector((state) => state.prospects.pagesCount);
    // const currentPage = useAppSelector((state) => state.prospects.page);

    const [moveToCampaignID, setMoveToCampaignID] = useState('');
    const [shouldDisable, setShouldDisable] = useState(true);
    const [canMoveProspects, setCanToMoveProspects] = useState<CanMoveProps>({} as CanMoveProps);

    const isDisabled =
        shouldDisable || (!moveAndPurchase && canMoveProspects?.allowedToMove?.length === 0) || movingProspects;

    const handleClose = () => {
        setMoveToCampaignID('');
        onModalClose();
    };

    const handleChange = (event) => {
        setMoveToCampaignID(event.target.value);
        setShouldDisable(false);
    };

    const moveProspectsToCampaign = () => {
        dispatch(handleMoveProspects(false));
    };

    const purchaseProspectsToOtherCampaign = () => {
        if (selectedProspects?.length)
            dispatch(
                purchaseProspects({
                    ids: [...selectedProspects],
                    toOtherCampaignId: moveToCampaignID,
                    navigate,
                })
            );
        else
            dispatch(
                purchaseProspects({
                    toOtherCampaignId: moveToCampaignID,
                    navigate,
                })
            );
        handleClose();
    };

    const handleMove = async (moveAndPurchase: boolean) => {
        dispatch(handleMoveProspects(true));

        if (moveAndPurchase) {
            await purchaseProspectsToOtherCampaign();
            onMoveProspects();
            return;
        }

        await moveProspectsToCampaign();
        onMoveProspects();
        handleClose();
    };

    const getSubmitButtonValue = (): string => {
        if (movingProspects) {
            return 'Moving';
        } else {
            if (moveAndPurchase) {
                return 'Add';
            } else {
                return 'Move';
            }
        }
    };

    return (
        <>
            <Modal open onClose={handleClose} onClick={(event) => event.stopPropagation()}>
                <MoveProspectsToCampaignContainer>
                    <div className="modal-container">
                        <div className="input-container">
                            <div>
                                <IconButton
                                    onClick={handleClose}
                                    disableRipple
                                    className="close-icon"
                                    data-Cy="close-icon"
                                    size="large"
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Typography variant="h3" className="title margin-bottom">
                                    {moveAndPurchase ? ' Add to collection' : 'Move to collection '}
                                </Typography>
                            </div>
                            <div>
                                <Typography className="typography title-small">Select prospect collection</Typography>
                                <FormControl className="form-control" variant="filled">
                                    <Select
                                        value={moveToCampaignID}
                                        onChange={handleChange}
                                        displayEmpty
                                        disableUnderline
                                        className={moveToCampaignID === '' ? 'disabled' : 'active'}
                                        MenuProps={{ style: { maxWidth: '1px' } }} // kinda hacky, but it works for line MenuItem span
                                        data-cy="select-campaign-dropdown"
                                    >
                                        <MenuItem value="" disabled>
                                            Select...
                                        </MenuItem>
                                        {prospectCollections &&
                                            prospectCollections.map(
                                                ({ name, id }) =>
                                                    id !== campaignId && (
                                                        <MenuItem
                                                            key={id}
                                                            value={id}
                                                            data-id={id}
                                                            id={id}
                                                            className="option"
                                                        >
                                                            <span
                                                                style={{
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                {name}
                                                            </span>
                                                        </MenuItem>
                                                    )
                                            )}
                                    </Select>
                                </FormControl>
                            </div>
                            {shouldShowNumbers && (
                                <div>
                                    {!moveAndPurchase && (
                                        <Typography className="additional-info bold">
                                            {`${
                                                Object.keys(canMoveProspects).length > 0 &&
                                                canMoveProspects?.allowedToMove.length > 0
                                                    ? canMoveProspects?.allowedToMove.length
                                                    : 0
                                            } / ${selectedProspects.length} prospects will be moved`}
                                        </Typography>
                                    )}
                                </div>
                            )}
                        </div>
                        <Divider className="divider" />
                        <div className="buttons-container">
                            <Button
                                className="button button-outlined-blue "
                                variant="outlined"
                                data-cy="close-button"
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                            <Button
                                className={`${isDisabled ? 'disabled' : 'button-blue'} button`}
                                disabled={isDisabled}
                                variant="contained"
                                endIcon={movingProspects && <CircularProgress color="inherit" size={20} />}
                                onClick={() => handleMove(!!moveAndPurchase)}
                                data-cy="move-button"
                            >
                                {getSubmitButtonValue()}
                            </Button>
                        </div>
                    </div>
                </MoveProspectsToCampaignContainer>
            </Modal>
        </>
    );
}
